import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Trash2, MessageCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const URL_G = /(https?:\/\/[^\s<>"')]+)/gi;
const linkify = (t) => t.split(URL_G).map((p, i) => (/^https?:\/\//i.test(p)
  ? <a key={i} href={p} target="_blank" rel="nofollow ugc noopener noreferrer" className="underline text-rose-italic break-all">{p}</a> : <span key={i}>{p}</span>));

const Message = ({ m, canDelete, onDelete }) => (
  <div className="group flex gap-2 text-sm" data-testid={`chat-msg-${m.id}`}>
    <div className="w-7 h-7 rounded-full bg-rose-soft text-mahogany text-[11px] flex items-center justify-center shrink-0 font-serif-display">{(m.author_name || '?').split(' ').map((x) => x[0]).slice(0, 2).join('')}</div>
    <div className="min-w-0 flex-1">
      <span className="font-medium text-burgundy mr-1.5">{m.author_name}</span>
      <span className="text-burgundy/85 break-words">{linkify(m.text)}</span>
    </div>
    {canDelete && <button onClick={() => onDelete(m)} className="opacity-0 group-hover:opacity-100 text-mahogany/50 hover:text-red-600 transition-opacity" aria-label="Delete message" data-testid={`chat-delete-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
  </div>
);

export const LiveChat = ({ sessionId, hostUserId, className = '' }) => {
  const { user, hasRole } = useAuth();
  const { toast } = useToast();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const box = useRef(null);
  const load = useCallback(() => api.get(`/live/${sessionId}/chat`).then((r) => setMsgs(r.data || [])).catch(() => {}), [sessionId]);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [msgs.length]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try { await api.post(`/live/${sessionId}/chat`, { text }); setText(''); load(); }
    catch (er) { toast({ title: 'Message not sent', description: er?.response?.data?.detail?.[0]?.msg || er?.response?.data?.detail }); } finally { setSending(false); }
  };
  const del = async (m) => { try { await api.delete(`/live/chat/${m.id}`); load(); } catch (er) { toast({ title: 'Failed', description: er?.response?.data?.detail }); } };
  const mod = user && (hasRole('admin') || user.user_id === hostUserId);
  return (
    <div className={`flex flex-col bg-white/80 rounded-2xl border border-rose/40 overflow-hidden h-[420px] lg:h-auto ${className}`} data-testid={`live-chat-${sessionId}`}>
      <div className="px-4 py-3 border-b border-rose/30 flex items-center gap-2 text-burgundy font-medium"><MessageCircle className="w-4 h-4 text-rose-italic" /> Live reflections</div>
      <div ref={box} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0" data-testid="live-chat-messages">
        {msgs.length === 0 ? <p className="text-xs text-mahogany/50 text-center mt-6">Be the first to share a reflection.</p>
          : msgs.map((m) => <Message key={m.id} m={m} canDelete={mod || (user && user.user_id === m.author_id)} onDelete={del} />)}
      </div>
      {user ? (
        <form onSubmit={send} className="p-3 border-t border-rose/30 flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Share a reflection…" className="flex-1 min-w-0 px-3 py-2 rounded-full border border-rose/50 bg-white text-sm text-burgundy" data-testid="live-chat-input" />
          <button disabled={sending} className="w-9 h-9 rounded-full bg-mahogany text-cream flex items-center justify-center shrink-0" aria-label="Send" data-testid="live-chat-send">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
        </form>
      ) : (
        <div className="p-3 border-t border-rose/30 text-xs text-mahogany/70 text-center"><Link to="/login" className="underline text-rose-italic" data-testid="live-chat-signin">Sign in</Link> to join the chat</div>
      )}
    </div>
  );
};

export default LiveChat;
